import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { FinancialsService } from 'src/app/services/modules/financials/financials.service';
import { environment } from 'src/environments/environment';
import { formatMoney } from '../financial/financial-grid';

export interface DisbursementItem {
  booking_id: number;
  listing_id: number;
  item_label: string;
  total: string;
  /** Relative listing link, e.g. "/listing/cars/930". */
  url: string;
}

export interface Disbursement {
  disbursement_id: number;
  amount: string;
  reference: string | null;
  created_at_utc: string | null;
  /** "YYYY-MM-DD HH:mm:ss", used when created_at_utc is missing. */
  disbursed_at: string | null;
  status: string;
  currency: string;
  description: string | null;
  items: DisbursementItem[];
}

export const DISBURSEMENTS_PAGE_SIZE = 10;

/** The payout date shown in the header: created_at_utc as a local day, else the day part of disbursed_at. */
export function disbursementDate(row: Pick<Disbursement, 'created_at_utc' | 'disbursed_at'>): string {
  if (row.created_at_utc) {
    const date = new Date(row.created_at_utc);
    if (!Number.isNaN(date.getTime())) {
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${date.getFullYear()}-${month}-${day}`;
    }
  }
  return row.disbursed_at ? row.disbursed_at.slice(0, 10) : '';
}

@Component({
  selector: 'app-disbursements',
  templateUrl: './disbursements.component.html',
  styleUrls: [
    './disbursements.component.scss',
  ],
})
export class DisbursementsComponent implements OnInit, OnDestroy {
  rows: Disbursement[] = [];
  page = 1;
  pageCount = 1;
  recordCount = 0;
  loading = true;
  error = false;
  /** Decimal string from the wallet endpoint, kept as sent; null before it arrives or when it fails. */
  amountHeld: string | null = null;
  walletLoading = true;
  readonly amountHeldHelp =
    'Escrow still held for jobs in progress (booked, collected or delivered by the provider but not yet completed), ' +
    'including accepted price increases and less price decreases. It has not been released yet, so it is not the same as ' +
    'Pending Payout or the payouts listed here.';
  /** Ids of the open panels. */
  private readonly open = new Set<number>();
  private request?: Subscription;
  private walletRequest?: Subscription;

  constructor(private financialsService: FinancialsService) {}

  ngOnInit(): void {
    // Two separate requests, so one failing never blocks the other; the wallet does not follow the paging.
    this.loadWallet();
    this.load(1);
  }

  ngOnDestroy(): void {
    this.request?.unsubscribe();
    this.walletRequest?.unsubscribe();
  }

  private loadWallet(): void {
    this.walletRequest = this.financialsService.getProviderWallet().subscribe({
      next: (res: any) => {
        const value = res?.status === false ? null : res?.amount_held;
        this.amountHeld = value === null || value === undefined || value === '' ? null : String(value);
        this.walletLoading = false;
      },
      error: () => {
        this.walletLoading = false;
      },
    });
  }

  load(page: number): void {
    this.request?.unsubscribe();
    this.loading = true;
    this.error = false;
    this.request = this.financialsService.getProviderDisbursements({ page, pagesize: DISBURSEMENTS_PAGE_SIZE }).subscribe({
      next: (res: any) => {
        this.rows = Array.isArray(res?.data) ? res.data : [];
        // A page past the end comes back as the last page, so the response is the truth.
        this.page = Number(res?.page) || page;
        this.pageCount = Math.max(Number(res?.page_count) || 1, 1);
        this.recordCount = Number(res?.record_count) || 0;
        this.open.clear();
        this.loading = false;
      },
      error: () => {
        this.error = true;
        this.loading = false;
      },
    });
  }

  goTo(page: number): void {
    if (page < 1 || page > this.pageCount || page === this.page || this.loading) {
      return;
    }
    this.load(page);
  }

  toggle(row: Disbursement): void {
    if (!this.open.delete(row.disbursement_id)) {
      this.open.add(row.disbursement_id);
    }
  }

  isOpen(row: Disbursement): boolean {
    return this.open.has(row.disbursement_id);
  }

  money(value: unknown): string {
    return formatMoney(value) || '—';
  }

  date(row: Disbursement): string {
    return disbursementDate(row);
  }

  /** The listing link is relative to the main Truckit site. */
  listingUrl(item: DisbursementItem): string | null {
    if (!item.url) {
      return null;
    }
    try {
      // Resolved against the site, so "/listing/cars/930" and "listing/cars/930" both work; a link that
      // points anywhere other than the Truckit site is not shown.
      const base = new URL(environment.appUrl);
      const link = new URL(item.url, base);
      return link.origin === base.origin ? link.href : null;
    } catch {
      return null;
    }
  }

  /** A booking can appear twice in one payout (base job + increase), so the index is part of the key. */
  itemKey(index: number, item: DisbursementItem): string {
    return `${item.booking_id}-${index}`;
  }

  rowKey(_: number, row: Disbursement): number {
    return row.disbursement_id;
  }
}
